import { storage } from '@/lib/utils/storage';
import { TOKEN_KEY } from '@/lib/utils/constants';
import { clavePorPortal, slugDeLaClave } from '@/lib/utils/portal-storage';

/**
 * Mini Prixia (27-oct): si la empresa de la sesión está probando por una
 * invitación de EGX One. Una empresa demo es una empresa REAL; esto sólo cambia lo
 * que muestra el menú (las reglas de verdad las pone el servidor).
 *
 * I6 de la revisión de T0: la marca va POR EMPRESA, como el token. Con una persona
 * en dos empresas y dos pestañas, la de la empresa real no puede ver el menú gris
 * de la demo. Se lee con la empresa del token que usa esta pestaña.
 */
const DEMO_KEY = 'empresa_demo';

/*
  Spec 344, momento 5: la conversión se hace desde el panel de plataforma y el comercio
  se entera en su próximo `/me` o login. `FUE_DEMO_KEY` sobrevive al cierre de sesión a
  propósito: puede volver días después. Una empresa que nunca fue demo no tiene ninguna
  de estas dos marcas, y por eso nunca pide el 3D.
*/
const FUE_DEMO_KEY = 'empresa_fue_demo';
const CONVERTIDA_KEY = 'empresa_recien_convertida';
export const EVENTO_PLAN_CONVERTIDO = 'prixia:plan-convertido';

function anotarConversion(esDemo: boolean | null | undefined, slug: string): void {
  const fueDemo = clavePorPortal(FUE_DEMO_KEY, slug);
  if (esDemo === true) {
    storage.set(fueDemo, true);
    return;
  }
  // Estricto: una respuesta sin el campo no dice que la empresa dejó de ser demo.
  if (esDemo !== false || storage.get<boolean>(fueDemo) !== true) return;
  storage.remove(fueDemo);
  storage.set(clavePorPortal(CONVERTIDA_KEY, slug), true);
  try {
    window.dispatchEvent(new Event(EVENTO_PLAN_CONVERTIDO));
  } catch {
    // Un aviso que no sale no puede romper el ingreso: la marca queda para la próxima carga.
  }
}

/** ¿La empresa de esta pestaña dejó de ser demo y todavía no se le mostró? */
export function recienConvertida(): boolean {
  return storage.get<boolean>(clavePorPortal(CONVERTIDA_KEY, slugDeLaClave(TOKEN_KEY))) === true;
}

export function olvidarQueFueDemo(): void {
  storage.remove(clavePorPortal(CONVERTIDA_KEY, slugDeLaClave(TOKEN_KEY)));
}

/**
 * Productos hechos para otros clientes (grupo «Otros productos» de
 * `backend/src/utils/module-groups.ts`): en la demo no se muestran, ni con candado.
 * Espejo de ese grupo: si cambia allá, cambiarlo acá.
 */
export const MODULOS_DE_OTROS_PRODUCTOS: readonly string[] = [
  'menu:pricer-datec',
  'menu:pricer-solar',
  'menu:demo-store',
];

/**
 * `esSoporte`: la sesión es de «Entrar como». Soporte mira la empresa de otra persona:
 * no anota ni consume las marcas de conversión, que son del comercio y no del admin.
 */
export function guardarEmpresaDemo(
  esDemo: boolean | null | undefined,
  slug: string | null,
  esSoporte = false
): void {
  storage.set(clavePorPortal(DEMO_KEY, slug), !!esDemo);
  // La marca global de la Tarea 0 ya no se lee: se borra para que no confunda.
  if (!slug) return;
  storage.remove(DEMO_KEY);
  if (esSoporte) return;
  anotarConversion(esDemo, slug);
}

export function olvidarEmpresaDemo(slug: string | null): void {
  storage.remove(clavePorPortal(DEMO_KEY, slug));
}

export function esEmpresaDemo(): boolean {
  return storage.get<boolean>(clavePorPortal(DEMO_KEY, slugDeLaClave(TOKEN_KEY))) === true;
}

/** En la demo, una entrada del menú fuera de lo incluido se ve con candado, salvo los productos de otros clientes. */
export function seVeConCandadoEnLaDemo(menuKey: string | undefined): boolean {
  return !!menuKey && !MODULOS_DE_OTROS_PRODUCTOS.includes(`menu:${menuKey}`);
}

/**
 * Pantallas que comparten la llave de lo que la demo incluye pero son del plan
 * completo (decisión 7 de Sebastián: «Modelos»; y «Tiendas conectadas»). El servidor también las corta.
 */
export const RUTAS_DEL_PLAN_COMPLETO_EN_LA_DEMO: readonly string[] = [
  '/pricing-models',
  // El servidor les da 403 a una demo (store-connections.routes.ts, soloFueraDeLaDemo): decisión de Sebastián, 2-nov.
  '/tiendas-conectadas',
  '/historial-push',
];

export function esRutaDelPlanCompletoEnLaDemo(href: string): boolean {
  return RUTAS_DEL_PLAN_COMPLETO_EN_LA_DEMO.some((r) => href === r || href.startsWith(`${r}/`));
}

/*
  Ronda Z (P1, decisión de Sebastián): en la demo «Mi empresa» abre SÓLO la pestaña
  Usuarios (invitar, ver y activar personas); el resto sigue como parte del plan completo.
  El servidor no tiene candado de módulos para Mi empresa (sólo rol y el tope de personas),
  así que esto es menú y pantalla. Se decide por la marca demo de la empresa, no por una
  empresa en particular ni por un rol.
*/
export const MI_EMPRESA = '/mi-empresa';

/**
 * La guarda de ruta deja pasar «Mi empresa» a una demo aunque no traiga el módulo `admin`
 * (decisión de Sebastián, 2-nov): la pestaña la decide `MiEmpresaPage` con
 * `SECCIONES_DE_MI_EMPRESA_EN_LA_DEMO`. Una empresa común sigue con el módulo, como su menú.
 */
export function miEmpresaAbreEnLaDemo(pathname: string): boolean {
  return esEmpresaDemo() && (pathname === MI_EMPRESA || pathname.startsWith(`${MI_EMPRESA}/`));
}

/** Las pestañas de Mi empresa que la prueba incluye. */
export const SECCIONES_DE_MI_EMPRESA_EN_LA_DEMO: readonly string[] = ['usuarios'];

/** ¿Esta pestaña de Mi empresa es del plan completo? Sólo cuenta en una empresa demo. */
export function esSeccionDelPlanCompletoEnLaDemo(seccion: string | undefined): boolean {
  return esEmpresaDemo() && !SECCIONES_DE_MI_EMPRESA_EN_LA_DEMO.includes(seccion ?? '');
}

/** ¿El enlace es la entrada «Mi empresa» del menú, que en la demo queda abierta? */
export function esEntradaAbiertaDeLaDemo(href: string): boolean {
  return href === MI_EMPRESA;
}

/** A dónde lleva una entrada del menú en la demo: «Mi empresa» entra por Usuarios. */
export function destinoEnLaDemo(href: string): string {
  return esEmpresaDemo() && href === MI_EMPRESA
    ? `${MI_EMPRESA}/${SECCIONES_DE_MI_EMPRESA_EN_LA_DEMO[0]}`
    : href;
}
